import { REPEAT_RULE_OPTIONS } from "@/consts";
import type {
  Activity_RepeatingActivity_Fragment,
  Activity_SingleActivity_Fragment,
  MeetingFragment,
  NewsUnion,
  RepeatingMeal,
  SingleMeal,
} from "@/graphql/generated-types";
import type { IOption } from "@/interfaces";
import { getStartDate } from "@/stories/utils/dates";
import type {
  EditRepeatTypes,
  RruleOptions,
  RRuleSupportedEntityType,
} from "@/types";
import toRRule from "@/helpers/rrules/toRRule";
import { RRule, RRuleSet, rrulestr } from "rrule";
import fromRRuletoRuleState from "@/helpers/rrules/fromRRuletoRuleState";

export type RRuleFeatureState = {
  id?: number;
  startDate: number;
  editRepeat: EditRepeatTypes;
  selectedRruleOption: IOption;
  rRule?: RRule | RRuleSet;
  repeatId?: string;
  rruleToSave?: RruleOptions;
  __typename: RRuleSupportedEntityType;
};

export type DatabaseModel = {
  __typename?:
    | Activity_RepeatingActivity_Fragment["__typename"]
    | Activity_SingleActivity_Fragment["__typename"]
    | MeetingFragment["__typename"]
    | RepeatingMeal["__typename"]
    | SingleMeal["__typename"]
    | NewsUnion["__typename"];
  repeat?: {
    __typename?: "RepeatRule" | undefined;
    id: string;
    rrule: string;
    rruleText: string;
  };
};

export type RRuleInput = {
  isRepeat: boolean;
  rrule?: string | null;
  repeat?: string | null;
};

const REVERSE_TYPE_MAP: Record<
  RRuleSupportedEntityType,
  RRuleSupportedEntityType
> = {
  SingleNews: "RepeatingNews",
  RepeatingNews: "SingleNews",
  SingleActivity: "RepeatingActivity",
  RepeatingActivity: "SingleActivity",
  SingleMeeting: "RepeatingMeeting",
  RepeatingMeeting: "SingleMeeting",
  SingleMeal: "RepeatingMeal",
  RepeatingMeal: "SingleMeal",
};

const REPEATING_TYPES: RRuleSupportedEntityType[] = [
  "RepeatingNews",
  "RepeatingActivity",
  "RepeatingMeeting",
  "RepeatingMeal",
];

export const RRULE_FEATURE = {
  getDefault(__typename: RRuleSupportedEntityType): RRuleFeatureState {
    return {
      __typename,
      startDate: getStartDate(),
      editRepeat: "single",
      selectedRruleOption: REPEAT_RULE_OPTIONS[0]!,
      rruleToSave: undefined,
    };
  },

  hash(state: RRuleFeatureState) {
    return {
      selectedRruleOption: state.selectedRruleOption,
      rruleToSave: state.rruleToSave,
    };
  },

  setState(data: DatabaseModel, state: RRuleFeatureState) {
    state.editRepeat = "single" as EditRepeatTypes;

    if (data.__typename?.toString().startsWith("Repeating") && data.repeat) {
      const rule = rrulestr(data.repeat.rrule);
      state.rruleToSave = fromRRuletoRuleState(rule);
      state.rRule = rule as RRule | RRuleSet;
      state.repeatId = data.repeat.id;
    }
  },

  getItem(state: RRuleFeatureState) {
    const result: Partial<RRuleInput> = {
      isRepeat: false,
      rrule: undefined,
      repeat: undefined,
    };

    // Setting the repeat rule for repeating activities
    if (state.rruleToSave) {
      state.rruleToSave.startDate = state.startDate;
      const rule = toRRule(state.rruleToSave);
      result.rrule = rule.toString();
      result.isRepeat = true;
      result.repeat = state.repeatId;
    }

    // We shouldn't marked as repeat on updates if it's not selected a repeat
    if (state.id && state.editRepeat === "single") {
      result.isRepeat = false;
      result.repeat = null;
      result.rrule = null;
    }

    return result;
  },

  resetTypename(state: RRuleFeatureState) {
    if (
      state.editRepeat === "single" &&
      REPEATING_TYPES.includes(state.__typename)
    ) {
      state.__typename = REVERSE_TYPE_MAP[state.__typename];
    }
  },
};
